import {
  getQuestionKindCapabilities,
  type QuestionKindCapabilities,
} from '@/shared-question-model';

import type { SpokenAffordances } from './shared-responder-v2-core-handsfree.types';
import {
  RESPONSE_MODES,
  type ResponseMode,
} from './shared-responder-v2-core-session.consts';
import type { ThreadKind } from './shared-responder-v2-core-thread.consts';
import type {
  MatrixRowValue,
  QuestionSpec,
  ResponderAnswer,
} from './shared-responder-v2-core-thread.types';

/** Draft mutations a surface can request. Reduced per kind by `reduceDraft`. */
export type ResponderDraftEvent =
  | { readonly type: 'textChanged'; readonly text: string }
  | { readonly type: 'transcriptChanged'; readonly transcript: string }
  | { readonly type: 'optionToggled'; readonly optionValue: string }
  | { readonly type: 'otherTextChanged'; readonly text: string }
  | { readonly type: 'ratingPicked'; readonly value: number }
  | {
      readonly type: 'matrixRowPicked';
      readonly subQuestionId: string;
      readonly value: MatrixRowValue;
    }
  | {
      readonly type: 'rankingReordered';
      readonly orderedOptionValues: readonly string[];
    }
  | { readonly type: 'spokenPick'; readonly optionValues: readonly string[] }
  | { readonly type: 'acknowledged' };

/**
 * A keystroke retires whatever dictation preceded it. `classifyChatMessage`
 * calls an answer spoken whenever a transcript survives, and the manager then
 * renders that transcript in place of the text — so a transcript left behind
 * by an edited dictation would display the wording the respondent corrected
 * away. Emitting the clear alongside every typed change is what keeps the
 * stored answer and the stored transcript from disagreeing.
 */
export function resolveTypedDraftEvents(
  text: string
): readonly ResponderDraftEvent[] {
  return [
    { type: 'textChanged', text },
    { type: 'transcriptChanged', transcript: '' },
  ];
}

/**
 * One question kind's behaviour — the immutable, no-`any` equivalent of a
 * subclass. The session reducer never switches on kind; it asks the behaviour.
 * Adding a question type is therefore one behaviour file, one surface file and
 * two registry lines, with no existing file growing. The capability flags
 * (`acceptsFreeText`, `hasOptionList`, ...) come from `@/shared-question-model`
 * — the same table the builder reads — so a kind's affordances cannot drift
 * between the two sides of the domain boundary.
 */
export interface ThreadBehaviour extends QuestionKindCapabilities {
  readonly kind: ThreadKind;
  /**
   * Whether voice is still recorded for this kind when the project contains a
   * URL or Task and is in text mode — the ticket's single exception, which only
   * open-ended questions explicitly set to voice may claim. Kept local: it
   * closes over responder-only session semantics the shared capability table
   * has no reason to know about.
   */
  readonly recordsVoiceWhenUrlPresent: boolean;
  /** Whether a backend follow-up on this kind is kept even though `supportsFollowUps` says no. */
  readonly retainsServerFollowUps: boolean;
  /** The answer is typed into the shell composer rather than picked on the question surface. */
  readonly answersInComposer: boolean;
  /** A completed pick on the question surface carries the responder onward with no Continue press. */
  readonly advancesOnSurfacePick: boolean;
  /** The question surface's own input is the same control the composer draws, so only one may render. */
  readonly surfaceInputIsComposer: boolean;
  /** False where the answer is only an acknowledgement, leaving nothing to change. */
  readonly isAnswerEditable: boolean;
  /**
   * Per-kind spoken affordances for hands-free delivery, as a partial: every
   * member not stated here is derived from the shared capability table by
   * `resolveSpokenAffordances`, so a kind only names an affordance it genuinely
   * disagrees with and no existing kind file had to change to gain one.
   */
  readonly spokenAffordances?: Partial<SpokenAffordances>;
  readonly emptyDraft: (spec: QuestionSpec) => ResponderAnswer | null;
  readonly reduceDraft: (
    draft: ResponderAnswer | null,
    event: ResponderDraftEvent,
    spec: QuestionSpec
  ) => ResponderAnswer | null;
  readonly isDraftComplete: (
    draft: ResponderAnswer | null,
    spec: QuestionSpec
  ) => boolean;
  readonly showsContinueButton: (
    spec: QuestionSpec,
    mode: ResponseMode
  ) => boolean;
}

/**
 * The safe default every kind's runtime behaviour starts from: inert, nothing
 * complete. A kind file states only its deltas from this plus the shared
 * capabilities, so a reviewer reads the difference rather than the whole
 * behaviour.
 */
const DEFAULT_RUNTIME_BEHAVIOUR: Omit<
  ThreadBehaviour,
  keyof QuestionKindCapabilities | 'kind'
> = {
  recordsVoiceWhenUrlPresent: false,
  retainsServerFollowUps: false,
  answersInComposer: false,
  advancesOnSurfacePick: false,
  surfaceInputIsComposer: false,
  isAnswerEditable: true,
  spokenAffordances: {},
  emptyDraft: () => null,
  reduceDraft: (draft) => draft,
  isDraftComplete: (draft) => draft !== null,
  /**
   * The general rule from the design sync: every non-required question gets a
   * Continue button in text mode so the responder can move on without
   * answering. Kinds that always show it (open-ended, task, message) override.
   */
  showsContinueButton: (spec, mode) =>
    mode === RESPONSE_MODES.Text && !spec.isRequired,
};

export function createThreadBehaviour(
  overrides: Pick<ThreadBehaviour, 'kind'> & Partial<ThreadBehaviour>
): ThreadBehaviour {
  const capabilities = getQuestionKindCapabilities(overrides.kind);

  return {
    ...capabilities,
    ...DEFAULT_RUNTIME_BEHAVIOUR,
    retainsServerFollowUps: capabilities.supportsFollowUps,
    ...overrides,
  };
}
